import { isNil, isUndefined } from 'lodash-es';

import { isNumericalMarker } from './is-numerical-marker.js';
import { serializeMarkerRange } from './serialize-marker-range.js';
import { WithId } from '../../../types/with-id.js';
import { DEFAULT_MARKER_PRIORITY } from '../constants.js';
import { parseDateNumberToNumber } from './parse-date-number-to-number.js';
import {
  AnnotationsMarkerConfig,
  NumericalMarkerProps,
  TimeMarkerProps,
} from '../types/annotations-marker-props.js';
import { AnnotationsTrackConfig } from '../types/annotations-track-props.js';
import { AnnotationsMarkerGroup } from '../types/annotations.js';

const areNumerical = (
  markers: AnnotationsMarkerConfig[],
): markers is WithId<AnnotationsMarkerConfig & NumericalMarkerProps>[] => {
  return isNumericalMarker(markers[0]);
};

const areTime = (
  markers: AnnotationsMarkerConfig[],
): markers is WithId<AnnotationsMarkerConfig & TimeMarkerProps>[] => {
  return !isNumericalMarker(markers[0]);
};

const toNumber = (value: number | Date) =>
  typeof value === 'number' ? value : value.getTime();

const outOfBounds = (
  marker: AnnotationsMarkerConfig,
  domain?: number[] | Date[],
) => {
  if (isUndefined(domain)) {
    return false;
  }

  const d0 = toNumber(domain[0]);
  const d1 = toNumber(domain[1]);
  const lowerBound = Math.min(d0, d1);
  const upperBound = Math.max(d0, d1);

  const start = toNumber(marker.start);
  const end = isNil(marker.end) ? start : toNumber(marker.end);

  return Math.min(start, end) > upperBound || Math.max(start, end) < lowerBound;
};

const handleNonOverlappingMarker = (
  processedMarkers: Map<string, AnnotationsMarkerGroup[]>,
  marker: AnnotationsMarkerConfig,
  markerKey: string,
) => {
  processedMarkers.set(markerKey, [createGroup(marker, markerKey)]);
};

const handleRepeatedAnnotation = (
  groups: AnnotationsMarkerGroup[],
  marker: AnnotationsMarkerConfig,
  markerKey: string,
): boolean => {
  const sameKeyGroup = groups.find(
    (group: AnnotationsMarkerGroup) => group.key === markerKey,
  );
  if (sameKeyGroup) {
    sameKeyGroup.markers.push(marker);
    const priority = marker.priority ?? DEFAULT_MARKER_PRIORITY;
    sameKeyGroup.priority = Math.max(sameKeyGroup.priority, priority);
    return true;
  }
  return false;
};

interface OverlapContext {
  processedMarkers: Map<string, AnnotationsMarkerGroup[]>;
  groups: AnnotationsMarkerGroup[];
  marker: AnnotationsMarkerConfig;
  markerKey: string;
  lastGroupStart: number;
  lastGroupEnd: number;
  markerStart: number;
  markerEnd: number;
  lastProcessedGroupKey: string;
  trackIndex: number;
}

const handleOverlappingMarker = (ctx: OverlapContext): string => {
  const updatedGroupsKey = `${ctx.trackIndex}_${[
    Math.min(ctx.lastGroupStart, ctx.markerStart),
    Math.max(ctx.lastGroupEnd, ctx.markerEnd),
  ].join('_')}`;
  if (updatedGroupsKey === ctx.lastProcessedGroupKey) {
    ctx.groups.push(createGroup(ctx.marker, ctx.markerKey));
    return ctx.lastProcessedGroupKey;
  }
  ctx.groups.push(createGroup(ctx.marker, ctx.markerKey));
  ctx.processedMarkers.set(updatedGroupsKey, ctx.groups);
  ctx.processedMarkers.delete(ctx.lastProcessedGroupKey);
  return updatedGroupsKey;
};

/**
 * Process annotation markers.
 * That includes sorting and grouping by time.
 *
 * @param trackProps - track props
 * @param trackIndex - track index
 * @param domain - visible domain
 * @returns processed markers
 */
export function processAnnotationTracksData(
  trackProps: AnnotationsTrackConfig,
  trackIndex: number,
  domain?: Date[] | number[],
): Map<string, AnnotationsMarkerGroup[]> {
  const { markers } = trackProps;
  const processedMarkers = new Map<string, AnnotationsMarkerGroup[]>();
  if (markers.length === 0) {
    return processedMarkers;
  }
  const sortedMarkers = sortMarkers(markers);
  let lastProcessedGroupKey = '';
  for (const marker of sortedMarkers) {
    if (marker.hidden || outOfBounds(marker, domain)) {
      continue;
    }
    const markerKey = serializeMarkerRange(marker, trackIndex);
    const markerStart = parseDateNumberToNumber(marker.start);
    const markerEnd = marker.end
      ? parseDateNumberToNumber(marker.end)
      : markerStart;
    if (!lastProcessedGroupKey) {
      handleNonOverlappingMarker(processedMarkers, marker, markerKey);
      lastProcessedGroupKey = markerKey;
      continue;
    }
    const [lastGroupStart, lastGroupEnd] = lastProcessedGroupKey
      .slice(lastProcessedGroupKey.indexOf('_') + 1)
      .split('_')
      .map(Number);
    const isOverlapping = overlapsWithTheLastGroup(
      lastGroupStart,
      lastGroupEnd,
      markerStart,
      markerEnd,
    );
    if (!isOverlapping) {
      handleNonOverlappingMarker(processedMarkers, marker, markerKey);
      lastProcessedGroupKey = markerKey;
      continue;
    }
    const groups = processedMarkers.get(lastProcessedGroupKey) ?? [];
    if (handleRepeatedAnnotation(groups, marker, markerKey)) {
      continue;
    }
    lastProcessedGroupKey = handleOverlappingMarker({
      processedMarkers,
      groups,
      marker,
      markerKey,
      lastGroupStart,
      lastGroupEnd,
      markerStart,
      markerEnd,
      lastProcessedGroupKey,
      trackIndex,
    });
  }
  return processedMarkers;
}

/**
 * Sorts markers depending of type
 *
 * @param markers -
 */
const sortMarkers = (markers: AnnotationsMarkerConfig[]) => {
  if (areNumerical(markers)) {
    return sortNumericalMarkers(markers);
  } else if (areTime(markers)) {
    return sortTimeMarkers(markers);
  } else {
    throw new Error('Unsupported type of markers');
  }
};

/**
 * Sorts markers by start value and end value.
 *
 * @param markers -
 */
const sortNumericalMarkers = (
  markers: WithId<AnnotationsMarkerConfig & NumericalMarkerProps>[],
): AnnotationsMarkerConfig[] => {
  return markers.sort((a, b) => {
    const aEnd = a.end ?? a.start;
    const bEnd = b.end ?? b.start;

    if (a.start === b.start) {
      return aEnd - bEnd;
    }

    return a.start - b.start;
  });
};

/**
 * Sorts markers by start time and end time.
 *
 * @param markers -
 */
const sortTimeMarkers = (
  markers: WithId<AnnotationsMarkerConfig & TimeMarkerProps>[],
): AnnotationsMarkerConfig[] => {
  return markers.sort((a, b) => {
    const aStart = a.start.getTime();
    const bStart = b.start.getTime();
    const aEnd = a.end ? a.end.getTime() : aStart;
    const bEnd = b.end ? b.end.getTime() : bStart;

    if (aStart === bStart) {
      return aEnd - bEnd;
    }

    return a.start.getTime() - b.start.getTime();
  });
};

/**
 * Creates a new group with a single annotation in it
 *
 * @param annotation - a single annotation to creat a group with
 * @param key - key of the group ("start_end" of the annotation)
 * @returns a new group with a single annotation in it
 */
const createGroup = (
  annotation: AnnotationsMarkerConfig,
  key: string,
): AnnotationsMarkerGroup => ({
  markers: [annotation],
  priority: annotation.priority ?? DEFAULT_MARKER_PRIORITY,
  key,
});

/**
 * Checks if the annotation overlaps with the last group
 *
 * @param groupStart - start of the last group
 * @param groupEnd - end of the last group
 * @param start - start of the annotation
 * @param end - end of the annotation
 */
const overlapsWithTheLastGroup = (
  groupStart: number,
  groupEnd: number,
  start: number,
  end: number,
): boolean =>
  (start >= groupStart && start <= groupEnd) ||
  (end >= groupStart && end <= groupEnd);
